// src/components/admin/training/packages/package-modal.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import Combobox from "@/components/ui/common/combobox";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { useGetCoachesQuery } from "@/services/coachApi";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import { useGetBusinessUnitsQuery } from "@/services/businessUnitApi";
import { useStaffBusinessUnits } from "@/hooks/use-staff-business-units";
import PriceMasterForm from "@/components/admin/price_master/modal/form";
import { PriceMaster, ItemType } from "@/types/system-setting/priceMaster";
import {
  useCreatePackageMutation,
  useUpdatePackageMutation,
} from "@/services/trainingApi";
import { toast } from "@/components/ui/use-toast";
import {
  TrainingPackage,
  TrainingPackageCreate,
  TrainingType,
  TrainingPackageStatus,
} from "@/types/training";
import { TaxGroupType } from "@/types/enums";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Badge } from "@/components/ui/badge";
import { formatToPKR } from "@/lib/helper";
import { Eye } from "lucide-react";

interface PackageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPackage?: TrainingPackage | null;
  onSuccess?: () => void;
}

/**
 * Each purchase creates total_credits / credits_per_session sessions, so the total must be
 * an exact multiple of the per-session credits (e.g. 20 / 3 would leave 2 unusable credits).
 * Returns an error message, or "" when the combination is valid / not yet complete.
 */
const getCreditsPerSessionError = (total: string, perSession: string): string => {
  const totalNum = Number(total);
  const perNum = Number(perSession);
  if (!total || !perSession || isNaN(totalNum) || isNaN(perNum) || totalNum <= 0 || perNum <= 0) {
    return "";
  }
  if (perNum > totalNum) return "Credits per session cannot exceed total credits.";
  const remainder = totalNum % perNum;
  if (remainder !== 0) {
    const lower = totalNum - remainder;
    const higher = lower + perNum;
    return `Total credits (${totalNum}) must be divisible by credits per session (${perNum}); ${remainder} credit${
      remainder === 1 ? "" : "s"
    } would be left unused. Use ${lower > 0 ? `${lower} or ` : ""}${higher} total credits.`;
  }
  return "";
};

const normalizeDurationValue = (val: any): string => {
  if (val === undefined || val === null || val === "") return "1.0";
  const num = Number(val);
  if (isNaN(num)) return "1.0";
  if (num === 60) return "1.0";
  if (num === 30) return "0.5";
  if (num === 45) return "0.75";
  if (num === 90) return "1.5";
  if (num === 120) return "2.0";
  if (Math.abs(num - 1.0) < 0.01) return "1.0";
  if (Math.abs(num - 0.5) < 0.01) return "0.5";
  if (Math.abs(num - 0.75) < 0.01) return "0.75";
  if (Math.abs(num - 1.5) < 0.01) return "1.5";
  if (Math.abs(num - 2.0) < 0.01) return "2.0";
  return String(num);
};

export const PackageModal: React.FC<PackageModalProps> = ({
  open,
  onOpenChange,
  selectedPackage,
  onSuccess,
}) => {
  const orgId = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_id
  ) || 0;

  const [createPackage, { isLoading: isCreating }] = useCreatePackageMutation();
  const [updatePackage, { isLoading: isUpdating }] = useUpdatePackageMutation();

  const { isLimitedToBusinessUnits, assignedBusinessUnitIds } = useStaffBusinessUnits();
  const { data: businessUnitsData } = useGetBusinessUnitsQuery(
    { org_id: Number(orgId), limit: 100 },
    { skip: !orgId || !open || !isLimitedToBusinessUnits }
  );

  const allowedFacilityCategoryIds = useMemo(() => {
    if (!isLimitedToBusinessUnits || assignedBusinessUnitIds.length === 0) return null;
    const bus = Array.isArray(businessUnitsData?.data) ? businessUnitsData.data : [];
    const assignedBUs = bus.filter((bu) => assignedBusinessUnitIds.includes(bu.id));
    const catIds = new Set<number>();
    assignedBUs.forEach((bu) => {
      (bu.facility_category_ids || []).forEach((id) => catIds.add(Number(id)));
    });
    return catIds;
  }, [isLimitedToBusinessUnits, businessUnitsData, assignedBusinessUnitIds]);

  // Queries for selectors
  const { data: coachesData } = useGetCoachesQuery(
    { org_id: Number(orgId), query: "is_trainer=true&limit=100&sort_key=id&sort_order=desc" },
    { skip: !orgId || !open }
  );

  const { data: facilitiesData } = useGetFacilitiesQuery(
    { orgId: Number(orgId), query: "status=active&limit=100&is_add_on=false" },
    { skip: !orgId || !open }
  );

  const { data: taxGroupData } = useGetTaxGroupQuery(
    { org_id: Number(orgId), query: "type=item" },
    { skip: !orgId || !open }
  );

  const { data: incomeCategoriesData } = useGetIncomeCategoryQuery(
    { org_id: Number(orgId), query: "limit=100&sort_key=id&sort_order=desc" },
    { skip: !orgId || !open }
  );

  // Fetch only training_package price master rules
  const { data: priceMastersData, isLoading: priceMasterLoading } = useGetPriceMastersQuery(
    { org_id: Number(orgId), query: "item_type=training_package&limit=100" },
    { skip: !orgId || !open }
  );

  // Form states
  const [name, setName] = useState("");
  const [trainingType, setTrainingType] = useState<TrainingType>(
    TrainingType.ONE_TO_ONE
  );
  const [coachId, setCoachId] = useState<string>("");
  const [facilityId, setFacilityId] = useState<string>("");
  const [priceMasterId, setPriceMasterId] = useState<string>("");
  const [price, setPrice] = useState<string>("");
  const [totalCredits, setTotalCredits] = useState<string>("10");
  const [creditsPerSession, setCreditsPerSession] = useState<string>("1");
  const sessionCount =
    totalCredits && creditsPerSession && !getCreditsPerSessionError(totalCredits, creditsPerSession) &&
    Number(creditsPerSession) > 0
      ? Number(totalCredits) / Number(creditsPerSession)
      : null;
  const [slotDurationHours, setSlotDurationHours] = useState<string>("1.0");
  const [validityDays, setValidityDays] = useState<string>("90");
  const [rescheduleCutoffHours, setRescheduleCutoffHours] = useState<string>("48");
  const [taxGroupId, setTaxGroupId] = useState<string>("");
  const [incomeCategoryId, setIncomeCategoryId] = useState<string>("");
  const [status, setStatus] = useState<boolean>(true);
  const [description, setDescription] = useState("");

  // Price Master Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | null>(null);

  // Validation error state for mandatory fields
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = Boolean(selectedPackage?.id);

  // Mapped options for searchable Comboboxes
  const coachesList = useMemo(
    () => (Array.isArray(coachesData?.data) ? coachesData.data : []),
    [coachesData]
  );
  const coachOptions = useMemo(() => {
    const filteredCoaches = coachesList.filter((coach: any) => {
      if (!allowedFacilityCategoryIds) return true;
      const cats: number[] = Array.isArray(coach.category_ids) ? coach.category_ids.map(Number) : [];
      if (cats.length === 0) return true;
      return cats.some((catId) => allowedFacilityCategoryIds.has(catId));
    });

    const options = filteredCoaches.map((coach: any) => {
      const name =
        coach.name ||
        `${coach.first_name || ""} ${coach.last_name || ""}`.trim() ||
        `Coach #${coach.id}`;
      const statusBadge = coach.coach_status === "pending" ? " (Pending)" : "";
      return {
        value: String(coach.id),
        label: `${name}${statusBadge}`,
      };
    });

    if (
      selectedPackage?.coach_id &&
      !options.some((opt) => opt.value === String(selectedPackage.coach_id))
    ) {
      options.unshift({
        value: String(selectedPackage.coach_id),
        label: selectedPackage.coach_name || `Coach #${selectedPackage.coach_id}`,
      });
    }

    return options;
  }, [coachesList, selectedPackage, allowedFacilityCategoryIds]);

  const facilitiesList = useMemo(
    () => (Array.isArray(facilitiesData?.data) ? facilitiesData.data : []),
    [facilitiesData]
  );
  const facilityOptions = useMemo(() => {
    return facilitiesList
      .filter((fac: any) => {
        if (fac.is_add_on) return false;
        const fType = String(fac.facility_type || "").toLowerCase();
        if (fType === "addon" || fType === "add_on") return false;
        const bType = String(fac.booking_type || "").toLowerCase();
        if (bType === "addon" || bType === "add_on") return false;
        if (allowedFacilityCategoryIds && fac.category_id) {
          if (!allowedFacilityCategoryIds.has(Number(fac.category_id))) {
            return false;
          }
        }
        return true;
      })
      .map((fac: any) => ({
        value: String(fac.id),
        label: fac.name,
        min_limit: fac.min_limit,
      }));
  }, [facilitiesList, allowedFacilityCategoryIds]);

  // Filter price master list to only training_package rules
  const priceMastersList = useMemo(() => {
    const raw = Array.isArray(priceMastersData?.data) ? priceMastersData.data : [];
    return raw.filter(
      (pm: any) =>
        pm.item_type === ItemType.TrainingPackage ||
        pm.item_type === "training_package"
    );
  }, [priceMastersData]);

  const priceMasterOptions = useMemo(() => {
    const options = priceMastersList.map((pm: any) => ({
      value: String(pm.id),
      label: pm.rule_code ? `${pm.rule_code} : ${pm.rule_name}` : pm.rule_name,
      price: pm.price,
    }));

    if (
      selectedPackage?.price_master_id &&
      !options.some((opt) => opt.value === String(selectedPackage.price_master_id))
    ) {
      const ruleCode = (selectedPackage as any).rule_code;
      const ruleName = (selectedPackage as any).price_master_name;
      const fallbackLabel = ruleCode
        ? `${ruleCode} : ${ruleName || "Price Master"}`
        : ruleName || `Rule #${selectedPackage.price_master_id}`;
      options.unshift({
        value: String(selectedPackage.price_master_id),
        label: fallbackLabel,
        price: selectedPackage.price,
      });
    }

    return options;
  }, [priceMastersList, selectedPackage]);

  const taxGroupsList = useMemo(() => {
    const raw = Array.isArray(taxGroupData?.data) ? taxGroupData.data : [];
    return raw.filter(
      (tg: any) => !tg.type || tg.type === TaxGroupType.Item || tg.type === "item"
    );
  }, [taxGroupData]);
  const taxGroupOptions = useMemo(() => {
    const options = taxGroupsList.map((tg: any) => ({
      value: String(tg.id),
      label: tg.name,
    }));

    if (
      selectedPackage?.tax_group_id &&
      !options.some((opt) => opt.value === String(selectedPackage.tax_group_id))
    ) {
      options.unshift({
        value: String(selectedPackage.tax_group_id),
        label: `Tax Group #${selectedPackage.tax_group_id}`,
      });
    }

    return options;
  }, [taxGroupsList, selectedPackage]);

  const incomeCategoriesList = useMemo(() => {
    if (Array.isArray(incomeCategoriesData?.data)) {
      return incomeCategoriesData.data;
    }
    if (Array.isArray(incomeCategoriesData)) {
      return incomeCategoriesData;
    }
    return [];
  }, [incomeCategoriesData]);

  const incomeCategoryOptions = useMemo((): { value: string; label: string }[] => {
    const options = incomeCategoriesList.map((inc: any) => ({
      value: String(inc.id),
      label: inc.name,
    }));

    if (
      selectedPackage?.income_category_id &&
      !options.some((opt) => opt.value === String(selectedPackage.income_category_id))
    ) {
      options.unshift({
        value: String(selectedPackage.income_category_id),
        label: `Income Category #${selectedPackage.income_category_id}`,
      });
    }

    return options;
  }, [incomeCategoriesList, selectedPackage]);

  // Synchronize on open or selectedPackage change
  useEffect(() => {
    if (selectedPackage && open) {
      setName(selectedPackage.name || "");
      setTrainingType(selectedPackage.training_type || TrainingType.ONE_TO_ONE);
      setCoachId(selectedPackage.coach_id ? String(selectedPackage.coach_id) : "");
      setFacilityId(
        selectedPackage.facility_id ? String(selectedPackage.facility_id) : ""
      );
      setPriceMasterId(
        selectedPackage.price_master_id
          ? String(selectedPackage.price_master_id)
          : ""
      );
      setPrice(
        selectedPackage.price !== undefined ? String(selectedPackage.price) : ""
      );
      setTotalCredits(String(selectedPackage.total_credits || 10));
      setCreditsPerSession(String(selectedPackage.credits_per_session || 1));
      setSlotDurationHours(normalizeDurationValue(selectedPackage.slot_duration_hours));
      setValidityDays(String(selectedPackage.validity_days || 90));
      setRescheduleCutoffHours(
        String(selectedPackage.reschedule_cutoff_hours ?? 48)
      );
      setTaxGroupId(
        selectedPackage.tax_group_id ? String(selectedPackage.tax_group_id) : ""
      );
      setIncomeCategoryId(
        selectedPackage.income_category_id
          ? String(selectedPackage.income_category_id)
          : ""
      );
      setStatus(selectedPackage.status === TrainingPackageStatus.ACTIVE);
      setDescription(selectedPackage.description || "");
      setErrors({});
    } else if (open) {
      setName("");
      setTrainingType(TrainingType.ONE_TO_ONE);
      setCoachId("");
      setFacilityId("");
      setPriceMasterId("");
      setPrice("");
      setTotalCredits("10");
      setCreditsPerSession("1");
      setSlotDurationHours("1.0");
      setValidityDays("90");
      setRescheduleCutoffHours("48");
      setTaxGroupId("");
      setIncomeCategoryId("");
      setStatus(true);
      setDescription("");
      setErrors({});
    }
  }, [selectedPackage, open]);

  // Handler for Price Master selection
  const handlePriceMasterSelect = (selectedLabel: string) => {
    const opt = priceMasterOptions.find((p) => p.label === selectedLabel);
    if (opt) {
      setPriceMasterId(opt.value);
      setPrice(String(opt.price ?? 0));
      setErrors((prev) => ({ ...prev, priceMasterId: "" }));
    } else {
      setPriceMasterId("");
      setPrice("");
    }
  };

  const handlePreview = (ruleId: number) => {
    const rule = priceMastersList.find((r: any) => r.id === ruleId);
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  // Handler for Facility selection: Auto-populate credits per session
  const handleFacilitySelect = (selectedLabel: string) => {
    const opt = facilityOptions.find((f) => f.label === selectedLabel);
    if (opt) {
      setFacilityId(opt.value);
      setErrors((prev) => ({ ...prev, facilityId: "" }));
      if (opt.min_limit !== undefined && opt.min_limit !== null && Number(opt.min_limit) > 0) {
        setCreditsPerSession(String(opt.min_limit));
        setErrors((prev) => ({
          ...prev,
          creditsPerSession: getCreditsPerSessionError(totalCredits, String(opt.min_limit)),
        }));
      }
    } else {
      setFacilityId("");
    }
  };

  // Handler for Coach selection
  const handleCoachSelect = (selectedLabel: string) => {
    const opt = coachOptions.find((c) => c.label === selectedLabel);
    if (opt) {
      setCoachId(opt.value);
      setErrors((prev) => ({ ...prev, coachId: "" }));
    } else {
      setCoachId("");
    }
  };

  // Handler for Tax Group selection
  const handleTaxGroupSelect = (selectedLabel: string) => {
    const opt = taxGroupOptions.find((t) => t.label === selectedLabel);
    if (opt) {
      setTaxGroupId(opt.value);
      setErrors((prev) => ({ ...prev, taxGroupId: "" }));
    } else {
      setTaxGroupId("");
    }
  };

  // Handler for Income Category selection
  const handleIncomeCategorySelect = (selectedLabel: string) => {
    const opt = incomeCategoryOptions.find((i) => i.label === selectedLabel);
    if (opt) {
      setIncomeCategoryId(opt.value);
      setErrors((prev) => ({ ...prev, incomeCategoryId: "" }));
    } else {
      setIncomeCategoryId("");
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = "Package Name is required.";
    if (!coachId) newErrors.coachId = "Coach assignment is required.";
    if (!facilityId) newErrors.facilityId = "Facility assignment is required.";
    if (!priceMasterId) newErrors.priceMasterId = "Price Master Rule is required.";
    if (!taxGroupId) newErrors.taxGroupId = "Tax Group is mandatory.";
    if (!incomeCategoryId) newErrors.incomeCategoryId = "Income Category is mandatory.";

    const credNum = Number(totalCredits);
    if (!totalCredits || isNaN(credNum) || credNum <= 0) {
      newErrors.totalCredits = "Total credits must be a positive number.";
    }

    const perSession = Number(creditsPerSession);
    if (!creditsPerSession || isNaN(perSession) || perSession <= 0) {
      newErrors.creditsPerSession = "Credits per session must be a positive number.";
    } else {
      const creditsError = getCreditsPerSessionError(totalCredits, creditsPerSession);
      if (creditsError) newErrors.creditsPerSession = creditsError;
    }

    const valDays = Number(validityDays);
    if (!validityDays || isNaN(valDays) || valDays <= 0) {
      newErrors.validityDays = "Validity days must be a positive number.";
    }

    const cutoff = Number(rescheduleCutoffHours);
    if (isNaN(cutoff) || cutoff < 0) {
      newErrors.rescheduleCutoffHours = "Cut-off hours must be 0 or greater.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill all mandatory fields before saving.",
      });
      return;
    }

    const payload: TrainingPackageCreate = {
      name: name.trim(),
      training_type: trainingType,
      coach_id: Number(coachId),
      facility_id: Number(facilityId),
      price_master_id: Number(priceMasterId),
      price: Number(price) || 0,
      total_credits: Number(totalCredits),
      credits_per_session: Number(creditsPerSession),
      slot_duration_hours: Number(normalizeDurationValue(slotDurationHours)) || 1.0,
      validity_days: Number(validityDays),
      reschedule_cutoff_hours: Number(rescheduleCutoffHours),
      tax_group_id: Number(taxGroupId),
      income_category_id: Number(incomeCategoryId),
      status: status ? TrainingPackageStatus.ACTIVE : TrainingPackageStatus.INACTIVE,
      description: description.trim() || null,
    };

    try {
      if (isEditing && selectedPackage?.id) {
        await updatePackage({ id: selectedPackage.id, data: payload }).unwrap();
        toast({
          variant: "success",
          title: "Package Updated",
          description: "Training package updated successfully.",
        });
      } else {
        await createPackage(payload).unwrap();
        toast({
          variant: "success",
          title: "Package Created",
          description: "Training package created successfully.",
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error Saving Package",
        description:
          err?.data?.message ||
          err?.data?.detail ||
          "Failed to save training package. Please review all fields.",
      });
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          hideCloseButton
          className="w-full !max-w-full sm:!max-w-[1100px] py-0 custom-scrollbar h-full"
        >
          <form noValidate className="pb-8" onSubmit={handleSave}>
            <SheetHeader className="sticky !top-0 z-40 pt-4 bg-white">
              <SheetTitle>
                <div className="flex flex-wrap justify-between gap-3 items-start bg-white">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-base sm:text-lg">
                        Training Package
                      </p>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold">
                        Dashboard
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold">
                        {isEditing ? "Edit" : "Add"} Training Package
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button"
                      className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                      variant="outline"
                      disabled={isCreating || isUpdating}
                      onClick={() => onOpenChange(false)}
                    >
                      <i className="fa fa-xmark"></i>
                      <span className="hidden sm:inline">Cancel</span>
                    </Button>

                    <LoadingButton
                      type="submit"
                      className="bg-primary text-black text-center flex items-center gap-2 h-9 px-3 sm:w-[120px]"
                      loading={isCreating || isUpdating}
                      disabled={isCreating || isUpdating}
                    >
                      {!isCreating && !isUpdating && (
                        <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                      )}
                      <span className="hidden sm:inline">
                        {isEditing ? "Update" : "Save"}
                      </span>
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
              <Separator className="h-[1px] rounded-full my-2" />
            </SheetHeader>

            {/* Section 1: Package Details */}
            <h1 className="font-semibold text-xl py-3">Package Details</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
              {/* Package Name */}
              <div>
                <FloatingLabelInput
                  id="pkg-name"
                  label="Package Name"
                  text="*"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setErrors((prev) => ({ ...prev, name: "" }));
                  }}
                  error={errors.name}
                />
              </div>

              {/* Training Type */}
              <div>
                <Select
                  value={trainingType}
                  onValueChange={(val) => setTrainingType(val as TrainingType)}
                >
                  <SelectTrigger floatingLabel="Training Type" text="*">
                    <SelectValue placeholder="Select Training Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TrainingType.ONE_TO_ONE}>
                      1-on-1 Personal Training
                    </SelectItem>
                    <SelectItem value={TrainingType.GROUP_PERSONAL}>
                      Group Personal Training
                    </SelectItem>
                    <SelectItem value={TrainingType.GROUP_TRAINING}>
                      Group Training
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Assigned Trainer / Coach */}
              <div>
                <Combobox
                  list={coachOptions}
                  defaultValue={coachOptions.find((c) => c.value === coachId)?.label || ""}
                  setFilter={handleCoachSelect}
                  label="Assigned Trainer / Coach *"
                />
                {errors.coachId && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.coachId}
                  </span>
                )}
              </div>

              {/* Assigned Facility / Studio */}
              <div>
                <Combobox
                  list={facilityOptions}
                  defaultValue={
                    facilityOptions.find((f) => f.value === facilityId)?.label || ""
                  }
                  setFilter={handleFacilitySelect}
                  label="Assigned Facility / Studio *"
                />
                {errors.facilityId && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.facilityId}
                  </span>
                )}
              </div>

              {/* Total Credits / Sessions */}
              <div>
                <FloatingLabelInput
                  id="total-credits"
                  type="number"
                  min="1"
                  label="Total Credits / Sessions"
                  text="*"
                  value={totalCredits}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTotalCredits(val);
                    setErrors((prev) => ({
                      ...prev,
                      totalCredits: "",
                      creditsPerSession: getCreditsPerSessionError(val, creditsPerSession),
                    }));
                  }}
                  error={errors.totalCredits}
                />
              </div>

              {/* Credits Per Session */}
              <div>
                <FloatingLabelInput
                  id="credits-per-session"
                  type="number"
                  min="1"
                  label="Credits Per Session"
                  text="*"
                  value={creditsPerSession}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCreditsPerSession(val);
                    setErrors((prev) => ({
                      ...prev,
                      creditsPerSession: getCreditsPerSessionError(totalCredits, val),
                    }));
                  }}
                  error={errors.creditsPerSession}
                />
                {!errors.creditsPerSession && sessionCount !== null && (
                  <span className="text-muted-foreground text-xs mt-1 block">
                    {sessionCount} {sessionCount === 1 ? "session" : "sessions"} will be created
                  </span>
                )}
              </div>

              {/* Package Time (Duration) */}
              <div>
                <Select
                  value={normalizeDurationValue(slotDurationHours)}
                  onValueChange={(val) => setSlotDurationHours(normalizeDurationValue(val))}
                >
                  <SelectTrigger floatingLabel="Package Time (Duration)" text="*">
                    <SelectValue placeholder="Select Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">30 Minutes (0.5 hr)</SelectItem>
                    <SelectItem value="0.75">45 Minutes (0.75 hr)</SelectItem>
                    <SelectItem value="1.0">60 Minutes (1.0 hr)</SelectItem>
                    <SelectItem value="1.5">90 Minutes (1.5 hrs)</SelectItem>
                    <SelectItem value="2.0">120 Minutes (2.0 hrs)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Validity Period (Days) */}
              <div>
                <FloatingLabelInput
                  id="validity-days"
                  type="number"
                  min="1"
                  label="Validity Period (Days)"
                  text="*"
                  value={validityDays}
                  onChange={(e) => {
                    setValidityDays(e.target.value);
                    setErrors((prev) => ({ ...prev, validityDays: "" }));
                  }}
                  error={errors.validityDays}
                />
              </div>

              {/* Reschedule Cut-off (Hours) */}
              <div>
                <FloatingLabelInput
                  id="reschedule-cutoff"
                  type="number"
                  min="0"
                  label="Reschedule Cut-off (Hours)"
                  text="*"
                  value={rescheduleCutoffHours}
                  onChange={(e) => {
                    setRescheduleCutoffHours(e.target.value);
                    setErrors((prev) => ({ ...prev, rescheduleCutoffHours: "" }));
                  }}
                  error={errors.rescheduleCutoffHours}
                />
              </div>
            </div>

            {/* Section 2: Pricing & Tax Classification */}
            <h1 className="font-semibold text-xl py-3 mt-4">
              Pricing & Revenue Classification
            </h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1 items-start">
              {/* Price Master Rule with Eye preview button */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Combobox
                    list={priceMasterOptions}
                    defaultValue={
                      priceMasterOptions.find((p) => p.value === priceMasterId)
                        ?.label || ""
                    }
                    setFilter={handlePriceMasterSelect}
                    label="Price Master Rule *"
                    disabled={priceMasterLoading}
                  />
                  {errors.priceMasterId && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {errors.priceMasterId}
                    </span>
                  )}
                </div>
                {priceMasterId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 shrink-0 mt-0.5"
                    onClick={() => handlePreview(Number(priceMasterId))}
                    title="Preview Price Master Rule"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Tax Group */}
              <div>
                <Combobox
                  list={taxGroupOptions}
                  defaultValue={
                    taxGroupOptions.find((t) => t.value === taxGroupId)?.label || ""
                  }
                  setFilter={handleTaxGroupSelect}
                  label="Tax Group *"
                />
                {errors.taxGroupId && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.taxGroupId}
                  </span>
                )}
              </div>

              {/* Income Category */}
              <div>
                <Combobox
                  list={incomeCategoryOptions}
                  defaultValue={
                    incomeCategoryOptions.find((i) => i.value === incomeCategoryId)
                      ?.label || ""
                  }
                  setFilter={handleIncomeCategorySelect}
                  label="Income Category *"
                />
                {errors.incomeCategoryId && (
                  <span className="text-red-500 text-xs mt-1 block">
                    {errors.incomeCategoryId}
                  </span>
                )}
              </div>
            </div>

            {/* Section 3: Additional Details */}
            <h1 className="font-semibold text-xl py-3 mt-4">
              Additional Information
            </h1>
            <div className="space-y-4 p-1">
              {/* Package Description */}
              <div>
                <FloatingLabelInput
                  id="pkg-description"
                  type="textarea"
                  rows={3}
                  label="Package Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              {/* Active Switch */}
              <div className="flex items-center justify-between p-3.5 rounded-lg border bg-slate-50/50">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">Package Status</p>
                  <p className="text-xs text-muted-foreground">
                    When active, this package is available for checkout in Point of Sale (POS).
                  </p>
                </div>
                <Switch checked={status} onCheckedChange={setStatus} />
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Price Master Preview Sheet */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto max-w-full sm:max-w-[700px] w-full p-0">
          <SheetHeader className="p-6 border-b bg-indigo-50/50">
            <SheetTitle className="text-xl font-bold text-indigo-900">
              Pricing Rule Preview
            </SheetTitle>
            <SheetDescription className="text-indigo-600">
              Viewing pre-configured pricing rule details
            </SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm
                isOpen={previewOpen}
                setOpen={setPreviewOpen}
                action="edit"
                data={selectedRuleData}
                readOnly={true}
                setData={() => {}}
                setAction={() => {}}
                refetch={() => {}}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PackageModal;